import React, { useEffect, useState, useCallback } from 'react';
import { Headphones, RefreshCcw, AlertTriangle, Volume2 } from 'lucide-react';
import { api, formatApiError, BACKEND_URL } from '../../lib/api';
import { toast } from 'sonner';

export function LifeStoryCard({ memorialId }) {
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState('');
  const [tone, setTone] = useState('gentle');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [audioBusy, setAudioBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/memorials/${memorialId}/life-story`);
      setState(data);
      if (data.draft) setDraft(data.draft);
      else if (data.published) setDraft(data.published);
      else setDraft('');
    } catch (e) {
      /* fresh memorial with no story yet — fine */
      setState({ status: 'none' });
    } finally {
      setLoading(false);
    }
  }, [memorialId]);

  useEffect(() => { load(); }, [load]);

  const wordCount = (draft.trim() ? draft.trim().split(/\s+/).length : 0);
  const status = state?.status || 'none';
  const isPublished = status === 'published' && state?.published;
  const draftMatchesPublished = isPublished && draft.trim() === (state.published || '').trim();

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/memorials/${memorialId}/life-story/generate`, { tone });
      setState(data);
      setDraft(data.draft || '');
      toast.success('Life Story drafted — review, edit, then publish.');
    } catch (e) { toast.error(formatApiError(e, 'Generation failed')); }
    finally { setBusy(false); }
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/memorials/${memorialId}/life-story/draft`, { text: draft });
      setState(data);
      toast.success('Draft saved.');
    } catch (e) { toast.error(formatApiError(e, 'Save failed')); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      // Save draft first so the published text matches whatever is in the editor
      if (draft.trim()) {
        await api.put(`/memorials/${memorialId}/life-story/draft`, { text: draft });
      }
      const { data } = await api.post(`/memorials/${memorialId}/life-story/publish`);
      setState(data);
      toast.success('Life Story published — it now appears on the memorial page.');
    } catch (e) { toast.error(formatApiError(e, 'Publish failed')); }
    finally { setBusy(false); }
  };

  const unpublish = async () => {
    if (!window.confirm('Remove the Life Story from the public memorial page?')) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/memorials/${memorialId}/life-story/unpublish`);
      setState(data);
      toast.success('Life Story hidden from the public page.');
    } catch (e) { toast.error(formatApiError(e, 'Unpublish failed')); }
    finally { setBusy(false); }
  };

  const discard = async () => {
    if (!window.confirm('Delete the current draft? This does not affect a previously-published Life Story.')) return;
    try {
      await api.delete(`/memorials/${memorialId}/life-story`);
      setState({ status: 'none' });
      setDraft('');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ---- Read-aloud audio tribute (OpenAI TTS, voice=sage, opt-in) ----
  const audio = state?.audio;
  // Cache-buster on <audio src> so a regenerated file is picked up without a refresh.
  const audioSrc = audio?.audio_url
    ? `${BACKEND_URL}${audio.audio_url}?v=${encodeURIComponent(audio.generated_at || '')}`
    : null;

  const generateAudio = async () => {
    setAudioBusy(true);
    try {
      await api.post(`/memorials/${memorialId}/life-story/audio`, {});
      await load();
      toast.success('Audio tribute ready.');
    } catch (e) {
      toast.error(formatApiError(e, 'Audio generation failed'));
    } finally {
      setAudioBusy(false);
    }
  };

  const removeAudio = async () => {
    if (!window.confirm('Remove the audio tribute from this memorial?')) return;
    setAudioBusy(true);
    try {
      await api.delete(`/memorials/${memorialId}/life-story/audio`);
      await load();
      toast.success('Audio removed.');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAudioBusy(false);
    }
  };

  if (loading) {
    return <div className="ml-card text-sm text-[hsl(var(--muted-foreground))]">Loading Life Story…</div>;
  }

  return (
    <div className="ml-card" data-testid="life-story-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-serif text-2xl text-[hsl(var(--primary))]">Life Story</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed max-w-prose">
            A short written paragraph about your loved one — grounded only in the life events and family you've entered. Generate a draft, read it, edit to taste, then publish when it feels right.
          </p>
        </div>
        <span
          data-testid="life-story-status"
          className={`text-[10px] tracking-[0.22em] uppercase font-medium px-2.5 py-1 rounded-sm whitespace-nowrap ${
            status === 'published'
              ? 'bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))]'
              : status === 'draft'
              ? 'bg-[hsl(var(--accent)/0.12)] text-[hsl(var(--accent))]'
              : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]'
          }`}
        >
          {status === 'published' ? 'Published' : status === 'draft' ? 'Draft' : 'Not yet written'}
        </span>
      </div>

      {/* Tone + Generate */}
      <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="life-story-tone-row">
        <span className="ml-overline">Tone</span>
        {['gentle', 'warm', 'quiet'].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTone(t)}
            aria-pressed={tone === t}
            data-testid={`life-story-tone-${t}`}
            className={`text-[11px] tracking-[0.18em] uppercase px-3 py-1.5 border transition-colors ${
              tone === t
                ? 'border-[hsl(var(--accent))] text-[hsl(var(--primary))]'
                : 'border-[hsl(var(--primary)/0.15)] text-[hsl(var(--primary)/0.6)] hover:text-[hsl(var(--primary))]'
            }`}
          >
            {t}
          </button>
        ))}
        <div className="flex-1" />
        <button
          type="button"
          onClick={generate}
          disabled={busy}
          className="ml-btn-gold !py-2 !px-4 text-xs"
          data-testid="life-story-generate-btn"
        >
          {busy ? 'Writing…' : state?.draft ? 'Regenerate draft' : 'Write with AI'}
        </button>
      </div>

      {/* Editor */}
      <div className="mt-5">
        <label className="ml-overline mb-2 block">Draft (editable)</label>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={8}
          placeholder="Generate a draft or write your own Life Story here…"
          className="ml-input w-full font-serif text-[16px] leading-relaxed text-[hsl(var(--primary))]"
          data-testid="life-story-textarea"
        />
        <div className="mt-2 flex items-center justify-between">
          <span className={`text-[11px] tracking-[0.18em] uppercase ${
            wordCount > 300 ? 'text-[hsl(var(--destructive))]' : 'text-[hsl(var(--primary)/0.5)]'
          }`} data-testid="life-story-wordcount">
            {wordCount} words{wordCount > 300 && ' — too long (max 300)'}
          </span>
          {state?.generated_at && (
            <span className="text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.4)]">
              AI draft · {new Date(state.generated_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        {!isPublished && (
          <>
            <button
              type="button"
              onClick={publish}
              disabled={busy || !draft.trim() || wordCount > 300}
              className="ml-btn-primary !py-2 !px-5 text-xs"
              data-testid="life-story-publish-btn"
            >
              Publish to memorial
            </button>
            <button
              type="button"
              onClick={saveDraft}
              disabled={busy || !draft.trim()}
              className="ml-btn-outline !py-2 !px-4 text-xs"
              data-testid="life-story-save-draft-btn"
            >
              Save draft
            </button>
          </>
        )}
        {isPublished && (
          <>
            <button
              type="button"
              onClick={publish}
              disabled={busy || draftMatchesPublished || !draft.trim() || wordCount > 300}
              className="ml-btn-primary !py-2 !px-5 text-xs"
              data-testid="life-story-republish-btn"
            >
              Update published story
            </button>
            <button
              type="button"
              onClick={unpublish}
              disabled={busy}
              className="ml-btn-outline !py-2 !px-4 text-xs"
              data-testid="life-story-unpublish-btn"
            >
              Hide from public page
            </button>
          </>
        )}
        {(state?.draft || state?.published) && (
          <button
            type="button"
            onClick={discard}
            disabled={busy}
            className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--destructive)/0.7)] hover:text-[hsl(var(--destructive))] hover:underline ml-auto"
            data-testid="life-story-delete-btn"
          >
            Delete Life Story
          </button>
        )}
      </div>

      {isPublished && !draftMatchesPublished && draft.trim() && (
        <div className="mt-4 text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--accent))]">
          Unsaved edits — click “Update published story” to replace what the public sees.
        </div>
      )}

      {/* ─────────────── Read-aloud audio tribute ───────────────
          iter94.10 — Rebuilt as a discoverable, state-aware card:
            • No audio yet → big gold "Generate Audio Narration" CTA
              with a one-line value prop so families understand WHY.
            • Audio + in sync → Listen / Regenerate / Last generated.
            • Story edited after audio → red "Out of sync" banner with
              one-click Regenerate. The pill at top doubles up so the
              warning is visible whether you're scanning or reading.
       */}
      {isPublished && (
        <div
          className="mt-8 pt-7 border-t border-[hsl(var(--primary)/0.08)]"
          data-testid="life-story-audio-card"
        >
          {/* —— Out-of-sync prominent banner (only when audio + edited) —— */}
          {audio && audio.out_of_sync && (
            <div
              className="mb-6 rounded-md border border-[hsl(var(--destructive)/0.45)] bg-[hsl(var(--destructive)/0.06)] px-4 py-3 flex items-start gap-3"
              data-testid="life-story-audio-oos-banner"
            >
              <AlertTriangle className="w-4 h-4 text-[hsl(var(--destructive))] shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--destructive))]">
                  Audio out of sync
                </div>
                <p className="text-[13px] text-[hsl(var(--primary)/0.85)] mt-1 leading-snug">
                  You edited the Life Story after the audio was recorded. Visitors won't see the Listen button until you regenerate.
                </p>
              </div>
              <button
                type="button"
                onClick={generateAudio}
                disabled={audioBusy}
                className="ml-btn-gold !py-2 !px-4 text-xs inline-flex items-center gap-2 shrink-0"
                data-testid="life-story-audio-oos-regen-btn"
              >
                <RefreshCcw className={`w-3.5 h-3.5 ${audioBusy ? 'animate-spin' : ''}`} />
                {audioBusy ? 'Regenerating…' : 'Regenerate now'}
              </button>
            </div>
          )}

          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-[hsl(var(--accent)/0.12)] text-[hsl(var(--accent))] flex items-center justify-center shrink-0">
                <Headphones size={16} strokeWidth={1.8} />
              </div>
              <div className="min-w-0">
                <h4 className="font-serif text-lg text-[hsl(var(--primary))]">
                  Read-aloud audio narration
                </h4>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1.5 leading-relaxed max-w-prose">
                  {audio
                    ? <>A calm, natural voice reading the published Life Story. Visitors will see a <span className="font-medium not-italic">Listen</span> button right under the story heading on the public memorial page.</>
                    : <>Add audio narration so visitors can <span className="font-medium not-italic">listen</span> to the Life Story instead of reading. Recorded in a calm, natural voice — takes about 30 seconds to generate.</>}
                </p>
              </div>
            </div>
            {audio && (
              <span
                className={`text-[10px] tracking-[0.22em] uppercase font-medium px-2.5 py-1 rounded-sm whitespace-nowrap ${
                  audio.out_of_sync
                    ? 'bg-[hsl(var(--destructive)/0.1)] text-[hsl(var(--destructive))]'
                    : 'bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))]'
                }`}
                data-testid="life-story-audio-status"
              >
                {audio.out_of_sync ? 'Out of sync' : 'Ready'}
              </span>
            )}
          </div>

          {/* —— No-audio state: prominent gold CTA —— */}
          {!audio && (
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={generateAudio}
                disabled={audioBusy}
                className="ml-btn-gold inline-flex items-center gap-2"
                data-testid="life-story-audio-generate-btn"
              >
                <Volume2 className="w-4 h-4" />
                {audioBusy ? 'Recording…' : 'Generate audio narration'}
              </button>
              <span className="text-[11px] text-[hsl(var(--primary)/0.6)]">
                Free. Reversible. About 30 seconds.
              </span>
            </div>
          )}

          {/* —— Has audio: inline player + Listen-style preview —— */}
          {audio && audioSrc && (
            <div className="mt-5">
              <audio
                src={audioSrc}
                controls
                preload="none"
                className="w-full"
                data-testid="life-story-audio-player"
              >
                Your browser does not support audio playback.
              </audio>
              {!audio.out_of_sync && (
                <p className="mt-3 text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.5)]">
                  Voice · {audio.voice || 'sage'}
                  {audio.generated_at && (
                    <> · Last generated {new Date(audio.generated_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</>
                  )}
                </p>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={generateAudio}
                  disabled={audioBusy}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary))] text-[11px] tracking-[0.18em] uppercase font-semibold hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition disabled:opacity-50"
                  data-testid="life-story-audio-regenerate-btn"
                >
                  <RefreshCcw className={`w-3.5 h-3.5 ${audioBusy ? 'animate-spin' : ''}`} />
                  {audioBusy ? 'Recording…' : 'Regenerate audio'}
                </button>
                <button
                  type="button"
                  onClick={removeAudio}
                  disabled={audioBusy}
                  className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--destructive)/0.7)] hover:text-[hsl(var(--destructive))] hover:underline"
                  data-testid="life-story-audio-delete-btn"
                >
                  Remove audio
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
